import { supabase } from '../lib/supabase';
import { LLMResponse, SearchResult } from '../types';
import { QueryIntent } from './queryAnalyzerLLM';

export interface ExplorationData {
  query: string;
  snapshot: {
    llmResponse: LLMResponse;
    searchResults: SearchResult[];
    intent: QueryIntent;
    theme?: any;
  };
}

export class HistoryService {
  /**
   * Save an exploration to the database
   */
  async saveExploration(data: ExplorationData): Promise<string | null> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        console.error('No user logged in');
        return null;
      }

      const { data: exploration, error } = await supabase
        .from('explorations')
        .insert({
          user_id: user.id,
          data: {
            query: data.query,
            snapshot: data.snapshot
          }
        })
        .select('id')
        .single();

      if (error) {
        console.error('Error saving exploration:', error);
        return null;
      }

      console.log('Exploration saved:', exploration.id);
      return exploration.id;
    } catch (error) {
      console.error('Error in saveExploration:', error);
      return null;
    }
  }

  /**
   * Get user's exploration history
   */
  async getUserHistory(limit: number = 20) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        console.error('No user logged in');
        return [];
      }

      const { data, error } = await supabase
        .from('explorations')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) {
        console.error('Error fetching history:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error in getUserHistory:', error);
      return [];
    }
  }

  /**
   * Get a specific exploration by ID
   */
  async getExploration(id: string) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        console.error('No user logged in');
        return null;
      }

      const { data, error } = await supabase
        .from('explorations')
        .select('*')
        .eq('id', id)
        .eq('user_id', user.id)
        .single();

      if (error) {
        console.error('Error fetching exploration:', error);
        return null;
      }

      return data;
    } catch (error) {
      console.error('Error in getExploration:', error);
      return null;
    }
  }

  /**
   * Delete an exploration
   */
  async deleteExploration(id: string): Promise<boolean> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        console.error('No user logged in');
        return false;
      }

      const { error } = await supabase
        .from('explorations')
        .delete()
        .eq('id', id)
        .eq('user_id', user.id);

      if (error) {
        console.error('Error deleting exploration:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error in deleteExploration:', error);
      return false;
    }
  }

  /**
   * Search history for related explorations
   */
  async searchHistory(query: string, limit: number = 5): Promise<any[]> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        console.log('No user found in searchHistory');
        return [];
      }

      const history = await this.getUserHistory(20);
      console.log('Total history items:', history.length);
      
      // Log first few history items for debugging
      if (history.length > 0) {
        console.log('First history item:', history[0]);
        console.log('History queries:', history.slice(0, 5).map(h => h.data?.query || 'No query'));
      }
      
      // Filter for related explorations
      const relatedExplorations = history.filter(exploration => {
        if (!exploration.data || !exploration.data.query) {
          console.log('Exploration missing data or query:', exploration);
          return false;
        }
        
        const exploreQuery = exploration.data.query.toLowerCase();
        const currentLower = query.toLowerCase();
        
        // Check if queries share any significant words (very basic matching)
        // The LLM will do the real semantic matching
        const currentWords = currentLower.split(/\s+/).filter((w: string) => w.length > 3);
        const exploreWords = exploreQuery.split(/\s+/).filter((w: string) => w.length > 3);
        
        const hasAnyWordOverlap = currentWords.some((word: string) => exploreWords.includes(word));
        
        const isRelated = hasAnyWordOverlap ||
               this.hasSharedConcepts(exploration.data.snapshot.llmResponse, query);
        
        if (isRelated) {
          console.log('Found related exploration:', exploration.data.query);
        }
        
        return isRelated;
      });

      console.log('Related explorations found:', relatedExplorations.length);
      // Return the most relevant ones
      return relatedExplorations.slice(0, limit);
    } catch (error) {
      console.error('Error searching history:', error);
      return [];
    }
  }

  /**
   * Check if an exploration has concepts related to the current query
   */
  private hasSharedConcepts(llmResponse: LLMResponse, currentQuery: string): boolean {
    const queryLower = currentQuery.toLowerCase();
    return llmResponse.concepts.some(concept => {
      const conceptLower = concept.text.toLowerCase();
      return conceptLower.includes(queryLower) || queryLower.includes(conceptLower);
    });
  }
}

// Export singleton instance
export const historyService = new HistoryService();